import type { ITicketRepository, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { Ticket, TicketComment, TicketStatus, User } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiTicketResponse {
  id: string;
  userId: string;
  userName?: string | null;
  unitId?: string | null;
  unitName?: string | null;
  title: string;
  description: string;
  category: 'LEAK' | 'LIGHTING' | 'SECURITY' | 'COMPLAINT' | 'LOST_PET' | 'OTHER';
  status: 'NEW' | 'IN_REVIEW' | 'IN_PROCESS' | 'RESOLVED';
  attachmentUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiCommentResponse {
  id: string;
  ticketId: string;
  userId: string;
  userName: string;
  text: string;
  isAdmin: boolean;
  createdAt: string;
}

interface ApiCreateTicketRequest {
  title: string;
  description: string;
  category: 'LEAK' | 'LIGHTING' | 'SECURITY' | 'COMPLAINT' | 'LOST_PET' | 'OTHER';
  attachmentUrl?: string;
  unitId?: string;
}

interface ApiUpdateTicketRequest {
  title?: string;
  description?: string;
  category?: 'LEAK' | 'LIGHTING' | 'SECURITY' | 'COMPLAINT' | 'LOST_PET' | 'OTHER';
  status?: 'NEW' | 'IN_REVIEW' | 'IN_PROCESS' | 'RESOLVED';
  attachmentUrl?: string;
}

interface ApiCreateCommentRequest {
  text: string;
}

// Mapea la respuesta de la API al tipo Ticket interno
const mapApiTicketToTicket = async (apiTicket: ApiTicketResponse): Promise<Ticket> => {
  const client = getApiClient();
  // Obtener comentarios del ticket
  let comments: TicketComment[] = [];
  try {
    const apiComments = await client.get<ApiCommentResponse[]>(`v1/tickets/${apiTicket.id}/comments`);
    comments = apiComments.map((c) => ({
      id: c.id,
      userId: c.userId,
      userName: c.userName,
      text: c.text,
      date: c.createdAt,
      isAdmin: c.isAdmin,
    }));
  } catch {
    // Si no hay comentarios, dejar array vacío
  }

  return {
    id: apiTicket.id,
    userId: apiTicket.userId,
    userName: apiTicket.userName ?? null,
    unitId: apiTicket.unitId ?? null,
    unitName: apiTicket.unitName ?? null,
    title: apiTicket.title,
    description: apiTicket.description,
    category: apiTicket.category as Ticket['category'],
    status: apiTicket.status as TicketStatus,
    date: apiTicket.createdAt,
    updatedAt: apiTicket.updatedAt,
    attachmentUrl: apiTicket.attachmentUrl || undefined,
    comments,
  };
};

export class ApiTicketRepository implements ITicketRepository {
  async getAllTickets(): Promise<Ticket[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiTicketResponse>>('v1/tickets');
    return Promise.all(response.data.map(mapApiTicketToTicket));
  }

  async getTickets(params?: QueryParams): Promise<PaginatedResponse<Ticket>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiTicketResponse>>(
      `v1/tickets?${queryString}`
    );
    const mappedData = await Promise.all(response.data.map(mapApiTicketToTicket));
    return {
      ...response,
      data: mappedData,
    };
  }

  async getUserTickets(userId: string, unitId?: string): Promise<Ticket[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiTicketResponse>>(
      `v1/tickets/my-tickets${unitId ? `?unitId=${encodeURIComponent(unitId)}` : ''}`
    );
    return Promise.all(response.data.map(mapApiTicketToTicket));
  }

  async createTicket(data: Omit<Ticket, 'id' | 'date' | 'comments' | 'updatedAt' | 'status'>): Promise<Ticket> {
    const client = getApiClient();
    const request: ApiCreateTicketRequest = {
      title: data.title,
      description: data.description,
      category: data.category,
      attachmentUrl: data.attachmentUrl,
      unitId: data.unitId ?? undefined,
    };
    const apiTicket = await client.post<ApiTicketResponse>('v1/tickets', request);
    return mapApiTicketToTicket(apiTicket);
  }

  async updateTicketStatus(ticketId: string, status: TicketStatus): Promise<void> {
    const client = getApiClient();
    const request: ApiUpdateTicketRequest = {
      status,
    };
    await client.put(`v1/tickets/${ticketId}`, request);
  }

  async addComment(ticketId: string, text: string, user: User): Promise<TicketComment> {
    const client = getApiClient();
    const request: ApiCreateCommentRequest = {
      text,
    };
    const apiComment = await client.post<ApiCommentResponse>(`v1/tickets/${ticketId}/comments`, request);
    return {
      id: apiComment.id,
      userId: apiComment.userId,
      userName: apiComment.userName,
      text: apiComment.text,
      date: apiComment.createdAt,
      isAdmin: apiComment.isAdmin,
    };
  }

  async getTicketById(id: string): Promise<Ticket | null> {
    const client = getApiClient();
    try {
      const apiTicket = await client.get<ApiTicketResponse>(`v1/tickets/${id}`);
      return await mapApiTicketToTicket(apiTicket);
    } catch {
      return null;
    }
  }

  async updateTicket(ticket: Ticket): Promise<Ticket> {
    const client = getApiClient();
    const request: ApiUpdateTicketRequest = {
      title: ticket.title,
      description: ticket.description,
      category: ticket.category,
      status: ticket.status,
      attachmentUrl: ticket.attachmentUrl,
    };
    const apiTicket = await client.put<ApiTicketResponse>(`v1/tickets/${ticket.id}`, request);
    return await mapApiTicketToTicket(apiTicket);
  }

  async deleteTicket(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/tickets/${id}`);
  }

  async getComments(ticketId: string): Promise<TicketComment[]> {
    const client = getApiClient();
    const comments = await client.get<ApiCommentResponse[]>(`v1/tickets/${ticketId}/comments`);
    return comments.map((comment) => ({
      id: comment.id,
      userId: comment.userId,
      userName: comment.userName,
      text: comment.text,
      date: comment.createdAt,
      isAdmin: comment.isAdmin,
    }));
  }
}

